import React from "react";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";

/* DEPRECATED. Please use /lib/util/helpers.js instead */

/**
 * TODO: use /lib/util/helpers.js instead, but first:
 *   change all imports of debounce() to use helpers.js
 *   move getResponsiveImageUrl() and toKebabCase() to helpers.js
 *   delete this file
 */

/**
 * Returns the URL of a media asset at a given index.
 * @param {Array} assets - The array of media assets.
 * @param {number} index - The index of the media asset.
 * @returns {string|null} The URL of the media asset or null if not found.
 */
const getSafeMediaAssetUrl = (assets, index = 0) => {
    if (assets?.length > index) {
        return assets[index].secure_url;
    }
    return null;
};

/**
 * Returns a CTA at a given index.
 * @param {Array} ctas - The array of CTAs.
 * @param {number} index - The index of the CTA.
 * @returns {Object|null} The CTA or null if not found.
 */
const getSafeCta = (ctas, index = 0) => {
    if (ctas?.length > index) {
        return ctas[index];
    }
    return null;
};

/**
 * Returns a debounced function.
 * @param {Function} func - The function to debounce.
 * @param {number} milliseconds - The debounce delay in milliseconds.
 * @returns {Function} The debounced function.
 */
const debounce = (func, milliseconds = 200) => {
    let timer;

    return (event) => {
        if (timer) {
            clearTimeout(timer);
        }

        timer = setTimeout(func, milliseconds, event);
    };
};

/**
 * Filters out invalid CTAs that do not have an internal or external link.
 * @param {Array} cta - The array of CTAs.
 * @returns {Array} The array of valid CTAs.
 */
const filterValidCta = (cta) =>
    Array.isArray(cta) ? cta.filter((link) => !!link.internalLink || !!link.externalUrl) : [];

/**
 * Renders a node based on its node type.
 * @param {Object} node - The node to render.
 * @returns {React.Element|null} The rendered node or null if the node type is not supported.
 */
const renderNode = (node) => {
    if (node) {
        switch (node.nodeType) {
            case "text":
                return <span>{node.value}</span>;

            case "paragraph":
                return <p>{node.content.map(renderNode)}</p>;

            case "list-item":
                return <li>{node.content.map(renderNode)}</li>;

            case "unordered-list":
                return <ul>{node.content.map(renderNode)}</ul>;

            case "document":
                return <div>{node.content.map(renderNode)}</div>;

            default:
                return null;
        }
    }
    return null;
};

/**
 * Calculates the reading statistics for an article based on its rich text content.
 *
 * @param {string} richTextContent - The rich text content of the article.
 * @param {number} wordsPerMinute - The average reading speed in words per minute (default is 150).
 * @returns {{wordCount: number, readTime: string}} - An object containing the word count and estimated reading time.
 */
const getArticleReadingStats = (richTextContent, wordsPerMinute = 150) => {
    try {
        if (!richTextContent || typeof richTextContent !== "string") {
            throw new Error("Rich text content is not supported");
        }

        const parsedRichContent = JSON.parse(richTextContent);
        const plainText = documentToPlainTextString(parsedRichContent);
        const wordCount = plainText.trim().split(/\s+/).length;
        const minutes = Math.ceil(wordCount / wordsPerMinute);

        return {
            wordCount,
            readTime: `${minutes} min read`,
        };
    } catch (error) {
        return {
            wordCount: 0,
            readTime: "1 min read",
        };
    }
};

/**
 * Extracts the 'src' attribute value from a script tag within a string.
 *
 * @param {string} content - The string containing the script tag.
 * @returns {string|null} - The 'src' attribute value if found, otherwise null.
 */
const convertStringToScriptSrc = (content) => {
    const scriptTagRegex = /<script.*?src=['"](.*?)['"].*?>/g;
    const match = scriptTagRegex.exec(content);

    if (match) {
        return match[1];
    }
    return null;
};

/**
 * Parses rich text content to plain text and extracts the 'src' attribute from any script tags.
 *
 * @param {string} content - The rich text content to parse.
 * @returns {string|null} - The 'src' attribute value of the first script tag if found, otherwise null.
 */
const getScriptSrc = (content) => {
    const parsedRichContent = JSON.parse(content);
    const plainText = documentToPlainTextString(parsedRichContent);
    const srcValue = convertStringToScriptSrc(plainText);

    return srcValue;
};

/**
 * Returns the page slug from the current window's location.
 * If the window object is not defined (e.g., in a server-side rendering context), it returns an empty string.
 * The function splits the pathname by the hostname and the query parameters, returning the first part as the slug.
 * @returns {string} The page slug or an empty string if the window object is not defined.
 */
const getPageSlug = () => {
    if (typeof window === "undefined") return "";

    const pathName = window.location?.pathname;
    return pathName.replace(/\//g, "").split("?")[0];
};

/**
 * getResponsiveImageUrl returns the secure url (the https url from cloudinary) for a given media
 * content entry from contentful
 * media[] {
 *   [0]
 *     .desktopMedia,
 *       .secure_url
 *     .mobileMedia
 *       .secure_url
 * }
 */
const getResponsiveImageUrl = (media) => {
    const isDesktop = typeof window !== "undefined" && window.innerWidth >= 992; // TODO: replace magic value
    const imageArray = isDesktop ? media?.[0]?.desktopMedia : media?.[0]?.mobileMedia;
    return imageArray?.[0]?.secure_url || "";
};

/*
 * Replace spaces and non-alphanumeric characters with hyphens
 */
function toKebabCase(inputString) {
    let kebabCaseString;
    if (typeof inputString === "string") {
        kebabCaseString = inputString
            .replace(/[^\w\s]/gi, "")
            .replace(/\s+/g, "-")
            .toLowerCase();
    }
    return kebabCaseString;
}

export {
    getSafeMediaAssetUrl,
    debounce,
    getSafeCta,
    filterValidCta,
    renderNode,
    getArticleReadingStats,
    getScriptSrc,
    getPageSlug,
    getResponsiveImageUrl,
    toKebabCase,
};
